import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { WhatsApp, Phone, PeopleAltOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import { tokens } from "../../../../../../theme";
import { CONTACT_ROLE_OPTIONS } from "../../../../constants/contact-roles";
import type { BookingData } from "../../../../types/view-booking";

interface BookingContactsCardProps {
  contacts?: BookingData["contacts"];
  loading?: boolean;
}

const roleLabel = (role: string) =>
  CONTACT_ROLE_OPTIONS.find((r) => r.value === role)?.label || role;

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function BookingContactsCard({
  contacts,
  loading,
}: BookingContactsCardProps) {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const rows = contacts ?? [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${colors.primary[300]}`,
        backgroundColor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <PeopleAltOutlined sx={{ color: colors.primary[400] }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Contacts
        </Typography>
        {!loading && (
          <Chip
            size="small"
            label={rows.length}
            sx={{ ml: 0.5, height: 20, fontSize: 11 }}
          />
        )}
      </Stack>

      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" spacing={2} alignItems="center">
              <Skeleton variant="circular" width={40} height={40} />
              <Stack spacing={0.5} flex={1}>
                <Skeleton variant="text" width="55%" height={18} />
                <Skeleton variant="text" width="35%" height={14} />
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: colors.neutral[400] }}>
          No contacts linked to this booking.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((c) => (
            <Stack
              key={c.id}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${colors.primary[300]}`,
              }}
            >
              <Avatar
                src={c.contact_image}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: colors.primary[400],
                  fontSize: 14,
                }}
              >
                {getInitials(c.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    noWrap
                  >
                    {c.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={roleLabel(c.role)}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 600,
                      color: colors.primary[400],
                      borderColor: colors.primary[400],
                    }}
                    variant="outlined"
                  />
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ color: colors.neutral[400], display: "block" }}
                  noWrap
                >
                  {[c.phone, c.email].filter((v) => v && v !== "N/A").join(" · ") ||
                    "No contact details"}
                </Typography>
              </Box>
              {c.phone && c.phone !== "N/A" && (
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    href={`tel:${c.phone}`}
                    sx={{ color: colors.primary[400] }}
                    aria-label={`call ${c.name}`}
                  >
                    <Phone fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={`https://wa.me/${c.phone.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "#25D366" }}
                    aria-label={`whatsapp ${c.name}`}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
}